import {
  StringValidator
} from "./chunk-D3MYLW7O.mjs";
import {
  AuthCodeNotFoundError,
  CsrfError,
  EnvironmentNotGivenError,
  InvalidHttpTokenResponseError,
  InvalidURLSearchParamsError,
  MissingSearchCodeParamError,
  MissingSearchStateParamError,
  NonceError,
  StatusCode1xxError,
  StatusCode3xxError,
  StatusCode4xxError,
  StatusCode5xxError
} from "./chunk-5XZTHQBU.mjs";
import {
  errorMessages
} from "./chunk-CXY23WTR.mjs";
import {
  Environment
} from "./chunk-U6P4P65O.mjs";

// src/utils/validators.ts
import { decodeJwt, decodeState } from "@inner-source/ciam-sdk";
function validateConfig(baseClass) {
  return class extends baseClass {
    constructor(...args) {
      const [
        { stateParams, redirectUrl, clientSecret, clientId, environment }
      ] = args;
      StringValidator.validateStringValue(
        stateParams.relyingParty,
        "relying-party"
      );
      StringValidator.validateStringValue(clientSecret, "clientSecret");
      StringValidator.validateStringValue(clientId, "clientId");
      StringValidator.validateStringValue(redirectUrl, "redirectUrl");
      StringValidator.validateUrl(redirectUrl);
      validateEnvironment(environment);
      super(...args);
    }
  };
}
function validateNonce(target, propertyKey, descriptor) {
  if (typeof descriptor.value !== "function") {
    throw new Error(errorMessages.NOT_A_FUNCTION_ERROR);
  }
  const originalMethod = descriptor.value;
  descriptor.value = function validateNonceMethod(data) {
    if (typeof data.id_token !== "string") {
      throw new Error(errorMessages.INVALID_ID_TOKEN);
    }
    const decodedNonce = decodeJwt(data.id_token);
    if (typeof decodedNonce.payload !== "object" || decodedNonce.payload === null || typeof decodedNonce.payload.nonce !== "string") {
      throw new Error(errorMessages.INVALID_NONCE_JWT_PAYLOAD);
    }
    const nonce = decodedNonce.payload.nonce;
    const instance = this;
    StringValidator.validateStringValue(nonce, "nonce");
    if (nonce !== instance.getNonce()) {
      throw new NonceError(errorMessages.INVALID_NONCE_DOES_NOT_MATCH);
    }
    return originalMethod.call(this, data);
  };
}
function validateResponseKeys(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validateResponse(data) {
    if (!data.access_token) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_ACCESS_TOKEN_RESPONSE_KEY
      );
    }
    if (!data.expires_in) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_EXPIRES_IN_RESPONSE_KEY
      );
    }
    if (!data.refresh_token) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_REFRESH_TOKEN_RESPONSE_KEY
      );
    }
    if (!data.token_type) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_TOKEN_TYPE_RESPONSE_KEY
      );
    }
    return originalMethod.call(this, data);
  };
}
function validateResponseKeysWithoutRT(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validateResponse(data) {
    if (!data.access_token) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_ACCESS_TOKEN_RESPONSE_KEY
      );
    }
    if (!data.expires_in) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_EXPIRES_IN_RESPONSE_KEY
      );
    }
    if (!data.token_type) {
      throw new InvalidHttpTokenResponseError(
        errorMessages.MISSING_TOKEN_TYPE_RESPONSE_KEY
      );
    }
    return originalMethod.call(this, data);
  };
}
var validParams = [
  "state",
  "code",
  "lastOp",
  "casl",
  "utm[utm_source]",
  "utm[utm_medium]",
  "utm[utm_campaign]",
  "utm[utm_term]",
  "utm[utm_content]",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content"
];
function validateURLSearchParams(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validatedMethod(search) {
    for (const key of search.keys()) {
      if (!validParams.includes(key)) {
        throw new InvalidURLSearchParamsError(`[${key}]: invalid Key.`);
      }
    }
    if (!search.get("code")) {
      throw new MissingSearchCodeParamError(
        errorMessages.MISSING_CODE_PARAM_ERROR
      );
    } else if (!search.get("state")) {
      throw new MissingSearchStateParamError(
        errorMessages.MISSING_STATE_PARAM_ERROR
      );
    }
    return originalMethod.call(this, search);
  };
}
function validateCsrf(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validatedMethod(search) {
    if (search.get("state")) {
      let state = search.get("state");
      try {
        state = decodeURIComponent(state);
        state = state.replace(/\s/g, "+");
      } catch (e) {
      }
      const { csrf } = decodeState(state);
      const instance = this;
      StringValidator.validateStringValue(csrf, "csrf");
      if (csrf !== instance.getCsrf()) {
        throw new CsrfError(errorMessages.INVALID_CSRF_DOES_NOT_MATCH);
      }
    }
    return originalMethod.call(this, search);
  };
}
function validateAuthCodeVerifierKey(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function validate() {
    const codeVerifier = originalMethod.call(this);
    if (!codeVerifier) {
      throw new AuthCodeNotFoundError(
        errorMessages.AUTH_CODE_VERIFIER_KEY_NOT_FOUND
      );
    }
    return codeVerifier;
  };
}
function validateResponseStatus(target, propertyKey, descriptor) {
  if (typeof descriptor.value !== "function") {
    throw new Error(errorMessages.NOT_A_FUNCTION_ERROR);
  }
  const originalMethod = descriptor.value;
  descriptor.value = function validate(HTTPRawResponse) {
    if (HTTPRawResponse.status >= 100 && HTTPRawResponse.status < 200) {
      throw new StatusCode1xxError(errorMessages.INVALID_1XX_ERRORS);
    } else if (HTTPRawResponse.status >= 300 && HTTPRawResponse.status < 400) {
      throw new StatusCode3xxError(errorMessages.INVALID_3XX_ERRORS);
    } else if (HTTPRawResponse.status >= 400 && HTTPRawResponse.status < 500) {
      throw new StatusCode4xxError(errorMessages.INVALID_4XX_ERRORS);
    } else if (HTTPRawResponse.status >= 500 && HTTPRawResponse.status < 600) {
      throw new StatusCode5xxError(errorMessages.INVALID_5XX_ERRORS);
    }
    return originalMethod.call(this, HTTPRawResponse);
  };
}
function validateRequest(target, propertyKey, descriptor) {
  if (typeof descriptor.value !== "function") {
    throw new Error(errorMessages.NOT_A_FUNCTION_ERROR);
  }
  const originalMethod = descriptor.value;
  descriptor.value = function validate(requestUrl, data, options = {}) {
    StringValidator.validateStringValue(requestUrl, "requestUrl");
    StringValidator.validateUrl(requestUrl);
    return originalMethod.call(this, requestUrl, data, options);
  };
}
function validateEnvironment(environment) {
  if (!Object.values(Environment).includes(environment)) {
    throw new EnvironmentNotGivenError(errorMessages.ENVIRONMENT_NOT_PRESENT);
  }
}

export {
  validateConfig,
  validateNonce,
  validateResponseKeys,
  validateResponseKeysWithoutRT,
  validateURLSearchParams,
  validateCsrf,
  validateAuthCodeVerifierKey,
  validateResponseStatus,
  validateRequest,
  validateEnvironment
};
